import type { Input } from '../sim/car';
import { ClockSync } from './netcode';
import { ClientMsg, OnlineSettings, PROTOCOL, RoomInfo, Roster, ServerMsg } from './protocol';

/**
 * The browser end of /ws (design.md B8.2): room messages, input batching
 * and a server-clock estimate. One instance per room visit.
 */

export interface StartInfo {
  seed: number;
  startAt: number;
  roster: Roster[];
  settings: OnlineSettings;
}

export interface ClientEvents {
  room(info: RoomInfo): void;
  start(s: StartInfo): void;
  input(slot: number, from: number, list: Input[]): void;
  gone(slot: number, from: number): void;
  error(msg: string, code?: string): void;
  desync(frame: number): void;
  closed(): void;
}

const localNow = () => performance.timeOrigin + performance.now();

export class OnlineClient {
  readonly clock = new ClockSync();
  private ws: WebSocket;
  private outFrom = -1;
  private out: [number, number, number][] = [];
  private pinger: number;
  private closedByUs = false;

  constructor(private ev: ClientEvents) {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.ws.onopen = () => this.ping();
    this.ws.onmessage = (e) => this.onMessage(e.data);
    this.ws.onclose = () => {
      window.clearInterval(this.pinger);
      if (!this.closedByUs) this.ev.closed();
    };
    this.ws.onerror = () => undefined;
    // Frequent pings early on (a quick clock estimate), then every 2 s.
    let n = 0;
    this.pinger = window.setInterval(() => {
      n++;
      if (n < 10 || n % 8 === 0) this.ping();
    }, 250);
  }

  /** Server time now (ms), as best we know it. */
  serverNow(): number {
    return localNow() + this.clock.offset;
  }

  get open(): boolean {
    return this.ws.readyState === WebSocket.OPEN;
  }

  private whenOpen(fn: () => void): void {
    if (this.ws.readyState === WebSocket.OPEN) fn();
    else this.ws.addEventListener('open', fn, { once: true });
  }

  create(name: string, color: number): void {
    this.whenOpen(() => this.send({ t: 'create', name, color, v: PROTOCOL }));
  }

  join(code: string, name: string, color: number): void {
    this.whenOpen(() => this.send({ t: 'join', code, name, color, v: PROTOCOL }));
  }

  send(m: ClientMsg): void {
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  /** Queue my input for tick `f` (sent in one message per frame by flush()). */
  queueInput(f: number, inp: Input): void {
    if (this.outFrom < 0) this.outFrom = f;
    this.out.push([inp.steer, inp.throttle, inp.brake]);
  }

  flush(): void {
    if (!this.out.length) return;
    for (let i = 0; i < this.out.length; i += 240) this.send({ t: 'in', f: this.outFrom + i, i: this.out.slice(i, i + 240) });
    this.outFrom = -1;
    this.out = [];
  }

  close(): void {
    this.closedByUs = true;
    window.clearInterval(this.pinger);
    try {
      this.send({ t: 'leave' });
      this.ws.close();
    } catch {
      /* already closed */
    }
  }

  private ping(): void {
    this.send({ t: 'ping', c: localNow() });
  }

  private onMessage(data: unknown): void {
    let m: ServerMsg;
    try {
      m = JSON.parse(String(data)) as ServerMsg;
    } catch {
      return;
    }
    switch (m.t) {
      case 'pong':
        this.clock.add(m.c, m.s, localNow());
        break;
      case 'room':
        this.ev.room(m.room);
        break;
      case 'start':
        this.ev.start({ seed: m.seed, startAt: m.startAt, roster: m.roster, settings: m.settings });
        break;
      case 'in':
        this.ev.input(
          m.s,
          m.f,
          m.i.map(([steer, throttle, brake]) => ({ steer, throttle, brake })),
        );
        break;
      case 'gone':
        this.ev.gone(m.s, m.f);
        break;
      case 'desync':
        this.ev.desync(m.f);
        break;
      case 'error':
        this.ev.error(m.msg, m.code);
        break;
    }
  }
}
